import { memo, useMemo, type FC } from 'react'
import { ScrollArea, Table, Text } from '@mantine/core'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import type { StreetSegmentPlanningData } from '@/api/main/types.gen'
import { buildItineraryRows } from './pathStats'
import type { PathState } from './types'

type ItineraryProps = {
  path: PathState
  segments: Record<string, StreetSegmentPlanningData>
}

export const Itinerary: FC<ItineraryProps> = memo(function Itinerary({ path, segments }) {
  const { t } = useTranslation('routePlanner')
  const rows = useMemo(() => buildItineraryRows(path.segments, segments), [path.segments, segments])

  if (isEmpty(path.segments)) {
    return (
      <Text size="sm" c="dimmed">
        {t('noSegmentsYet')}
      </Text>
    )
  }

  return (
    <ScrollArea.Autosize mah="calc(100vh - 220px)">
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>{t('segment')}</Table.Th>
            <Table.Th>{t('street')}</Table.Th>
            <Table.Th ta="right">{t('km')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(row => (
            <Table.Tr key={row.key}>
              <Table.Td>{row.index}</Table.Td>
              <Table.Td>
                <Text size="xs" ff="monospace">
                  {row.segmentId}
                </Text>
              </Table.Td>
              <Table.Td>{row.streetName}</Table.Td>
              <Table.Td ta="right">{row.lengthLabel}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea.Autosize>
  )
})

export default Itinerary
